import { Box, Flex, Progress, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { FaPlus } from 'react-icons/fa'
import { MdExpandLess, MdExpandMore, MdOutlineRuleFolder } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import { DescricaoCargo, iAssinates, iAssinaturas, iSolicitacaoReajuste } from '../../../../../interfaces'
import { contarItensSolicitados, iOrigemAjustes } from '../../../utils/ajustes'
import { dataExpirou, ordenarPorDataSolicitacao } from '../../../utils/data'
import { CardAssinante } from './components/CardAssinante'
import { ModalDocumentoSolicitacao } from './components/ModalDocumentoSolicitacao'

interface iProps {
  cargo: DescricaoCargo
  assinaturas: iAssinaturas[]
  onAdicionar: () => void
  onExcluir: (indice: number) => void
  onReenviar: (assinante: iAssinates) => void
  onBaixarPdf: (assinante: iAssinates) => void
  baixandoPdfDe?: string
}

const CARDS_VISIVEIS = 2

export interface iEntradaHistorico {
  solicitacao: iSolicitacaoReajuste
  anterior?: iOrigemAjustes
  quantidade: number
}

export const HistoricoAssinaturas: React.FC<iProps> = ({ cargo, assinaturas, onAdicionar, onExcluir, onReenviar, onBaixarPdf, baixandoPdfDe }) => {
  const nav = useNavigate()
  const [expandido, setExpandido] = useState(false)
  const [historicoAberto, setHistoricoAberto] = useState<number>()
  const [solicitacaoAberta, setSolicitacaoAberta] = useState<{ solicitacao: iSolicitacaoReajuste, anterior?: iOrigemAjustes }>()

  const solicitacoes = cargo.solicitacaoReajustes ?? []
  const ajustesPendentes = solicitacoes.filter(s => s.statusSolicitacao === 1).length

  function ultimaAssinaturaDe (email: string): iAssinaturas | undefined {
    const doAssinante = assinaturas.filter(a => a.emailAssinante === email)

    if (doAssinante.length === 0) return undefined

    return doAssinante.reduce((maisRecente, atual) =>
      new Date(atual.expiraEm) > new Date(maisRecente.expiraEm) ? atual : maisRecente
    )
  }

  // Pedidos de uma SolicitacaoAssinatura antiga da mesma pessoa (recadastro, por exemplo) nao
  // entram mais aqui: sem o vinculo por ID, so o e-mail decidia, e tudo virava uma coisa so.
  // Registros anteriores a essa mudanca nao tem o vinculo, entao caem para o e-mail mesmo.
  function solicitacoesDe (email: string, assinaturaAtualId?: string): iSolicitacaoReajuste[] {
    const doEmail = solicitacoes.filter(s => s.emailSolicitante === email)

    const daAssinaturaAtual = assinaturaAtualId === undefined
      ? doEmail
      : doEmail.filter(s => s.solicitacaoAssinaturaId === undefined || s.solicitacaoAssinaturaId === assinaturaAtualId)

    return ordenarPorDataSolicitacao(daAssinaturaAtual)
  }

  // Um mesmo e-mail pode ter varias linhas (reenvio, exclusao e recadastro), e a assinatura
  // nem sempre esta na de maior expiraEm, entao a data vem da linha assinada mais recente
  function assinadoEmDe (email: string): string | undefined {
    return assinaturas
      .filter(a => a.emailAssinante === email && a.assinadoEm)
      .map(a => a.assinadoEm as string)
      .sort((x, y) => new Date(y).getTime() - new Date(x).getTime())[0]
  }

  // A cadeia e montada sobre a lista completa: sem snapshot, o "antes" de um pedido e o
  // pedido anterior da mesma pessoa. So depois disso o que ficou vazio sai da tela.
  function historicoDe (email: string, assinaturaAtualId?: string): iEntradaHistorico[] {
    const todas = solicitacoesDe(email, assinaturaAtualId)

    return todas
      .map((solicitacao, i) => {
        const anterior = i > 0 ? todas[i - 1] : cargo

        return { solicitacao, anterior, quantidade: contarItensSolicitados(solicitacao, anterior) }
      })
      .filter(entrada => entrada.quantidade > 0 || (entrada.solicitacao.observacoes ?? '').trim() !== '')
  }

  function dataDoStatus (status: number, email: string, assinatura?: iAssinaturas, doAssinante?: iSolicitacaoReajuste[]): string | undefined {
    if (status === 2 || status === 3) return assinadoEmDe(email) ?? assinatura?.assinadoEm
    if (status === 1) return doAssinante?.[doAssinante.length - 1]?.dataSolicitacao ?? assinatura?.visualizadoEm

    return assinatura?.convidadoEm
  }

  // Sem prioridade por status: a unica ordenacao agora e a atividade mais recente da linha
  const linhas = (cargo.assinantes ?? []).map((assinante, indiceOriginal) => {
    const status = assinante.status ?? 0
    const assinatura = ultimaAssinaturaDe(assinante.email)
    const doAssinante = solicitacoesDe(assinante.email, assinatura?.id)
    const historico = historicoDe(assinante.email, assinatura?.id)
    const dataInteracao = dataDoStatus(status, assinante.email, assinatura, doAssinante)

    return { assinante, indiceOriginal, status, assinatura, doAssinante, historico, dataInteracao }
  })

  const assinantesOrdenados = [...linhas].sort((a, b) => {
    const tempoA = a.dataInteracao ? new Date(a.dataInteracao).getTime() : 0
    const tempoB = b.dataInteracao ? new Date(b.dataInteracao).getTime() : 0

    return tempoB - tempoA
  })

  const visiveis = expandido ? assinantesOrdenados : assinantesOrdenados.slice(0, CARDS_VISIVEIS)
  const ocultos = assinantesOrdenados.length - CARDS_VISIVEIS

  const total = assinantesOrdenados.length
  // Desatualizado nao conta: a assinatura existiu, mas o documento mudou depois dela
  const assinados = assinantesOrdenados.filter(({ assinante }) => assinante.status === 2).length
  const percentual = total > 0 ? Math.round((assinados / total) * 100) : 0

  return (
    <Flex
      flexDir={'column'}
      gap={'1.25rem'}
      w={'100%'}
      border={'1px solid var(--Gray5)'}
      borderRadius={'.375rem'}
      boxShadow={'var(--SombraBackground)'}
      p={'1.25rem'}
      mt={'1rem'}
    >
      <ModalDocumentoSolicitacao
        isOpen={solicitacaoAberta !== undefined}
        onClose={() => setSolicitacaoAberta(undefined)}
        solicitacao={solicitacaoAberta?.solicitacao}
        anterior={solicitacaoAberta?.anterior}
        nomeSolicitante={cargo.assinantes?.find(a => a.email === solicitacaoAberta?.solicitacao.emailSolicitante)?.nome}
      />

      <Flex justifyContent={'space-between'} alignItems={'flex-start'} gap={'1rem'} flexWrap={'wrap'}>
        <Box>
          <Text fontSize={'18px'} fontWeight={'600'} color={'var(--Gray1)'}>Assinaturas em andamento</Text>
          <Text fontSize={'13px'} color={'var(--Gray3)'} mt={'.125rem'}>Acompanhe o fluxo de aprovação deste cargo</Text>
        </Box>

        <Flex gap={'.75rem'} flexWrap={'wrap'}>
          <ButtonCmp
            OutlineColor={'OrangeEscuro'}
            leftIcon={<MdOutlineRuleFolder />}
            isDisabled={ajustesPendentes === 0}
            onClick={() => nav(`/Descritivo/Cargos/${cargo.id}/visualizarSolicitacoes`)}
          >
            Analisar ajustes ({ajustesPendentes})
          </ButtonCmp>
          <ButtonCmp VarColor={'Green2'} leftIcon={<FaPlus />} onClick={onAdicionar}>
            Nova Assinatura
          </ButtonCmp>
        </Flex>
      </Flex>

      {total > 0 && (
        <Box>
          <Flex justifyContent={'space-between'} alignItems={'center'} mb={'.375rem'}>
            <Text fontSize={'13px'} fontWeight={'500'} color={'var(--Gray2)'}>
              {assinados} de {total} {total === 1 ? 'assinatura' : 'assinaturas'}
            </Text>
            <Text fontSize={'12px'} color={'var(--Gray3)'}>{percentual}%</Text>
          </Flex>
          <Progress
            value={percentual}
            colorScheme={'green'}
            size={'sm'}
            borderRadius={'999px'}
            bgColor={'var(--Gray5)'}
          />
        </Box>
      )}

      {assinantesOrdenados.length === 0
        ? (
          <Box border={'1px dashed var(--Gray5)'} borderRadius={'.375rem'} p={'1.5rem'} textAlign={'center'}>
            <Text fontSize={'14px'} color={'var(--Gray3)'}>
              Nenhum assinante cadastrado para este cargo.
            </Text>
          </Box>
          )
        : (
          <Flex flexDir={'column'} gap={'1rem'} borderLeft={'2px solid var(--Gray5)'} ml={'1rem'}>
            {visiveis.map(({ assinante, indiceOriginal, status, assinatura, historico, dataInteracao }) => {
              return (
                <CardAssinante
                  key={indiceOriginal}
                  assinante={assinante}
                  status={status}
                  dataInteracao={dataInteracao}
                  tokenExpirado={assinatura !== undefined && dataExpirou(assinatura.expiraEm)}
                  historico={historico}
                  ultimaEntrada={historico.filter(e => e.solicitacao.statusSolicitacao === 1).slice(-1)[0]}
                  historicoAberto={historicoAberto === indiceOriginal}
                  onAlternarHistorico={() => setHistoricoAberto(historicoAberto === indiceOriginal ? undefined : indiceOriginal)}
                  onAbrirSolicitacao={setSolicitacaoAberta}
                  onReenviar={() => onReenviar(assinante)}
                  onExcluir={() => onExcluir(indiceOriginal)}
                  onBaixarPdf={() => onBaixarPdf(assinante)}
                  baixandoPdf={baixandoPdfDe === assinante.email}
                />
              )
            })}
          </Flex>
          )}

      {ocultos > 0 && (
        <Flex justifyContent={'center'}>
          <ButtonCmp
            OutlineColor={'Rosa'}
            leftIcon={expandido ? <MdExpandLess /> : <MdExpandMore />}
            onClick={() => setExpandido(!expandido)}
          >
            {expandido ? 'Ocultar histórico' : `Ver histórico completo (${ocultos})`}
          </ButtonCmp>
        </Flex>
      )}
    </Flex>
  )
}
